import * as Sentry from '@sentry/vue'
import { getPrinterStatusMessage } from '@/utils/printerStatusMessage'
import { openPrinterDiagnostic } from '@/views/composable/usePrinterDiagnostic'

const isProduction = () => process.env.NODE_ENV === 'production'

/**
 * プリンタ関連エラーを Sentry に送信する。
 * production 環境以外では何もしない（既存の console.error / トーストはそのまま）。
 *
 * 個人情報（顧客名・電話番号・注文内容など）は絶対に context へ含めないこと。
 *
 * @param {unknown} error - 例外オブジェクト
 * @param {object} context
 * @param {'receipt'|'kitchen'} context.kind - プリンタ種別
 * @param {'connect'|'print'|'print-response'|'connect-test'} context.phase - 発生フェーズ
 * @param {string} [context.ipAddress] - 接続先 IP
 * @param {number|string} [context.clubId] - 店舗 ID（currentClub.value?.id）
 * @param {string} [context.printerName] - キッチンプリンタ名など
 * @param {'qr-code'|'estimated'|'receipt-slip'|'settlement'|'debt-confirmation'|'accounting'} [context.slipKind] - レシートプリンタの slip 種別
 * @param {string|number} [context.responseCode] - onReceive 経由のときの ePOS 応答コード
 * @param {number} [context.status] - onReceive 経由のときの ePOS ステータスビットマスク
 * @param {string} [context.userMessage] - UI に表示したユーザ向けメッセージ
 */
export function reportPrinterError(error, context = {}) {
  if (!isProduction()) return

  const {
    kind, phase, ipAddress, clubId, printerName, slipKind, responseCode, status, userMessage,
  } = context

  Sentry.captureException(error, {
    tags: {
      feature: 'printer',
      kind,
      phase,
      ...(slipKind ? { slipKind } : {}),
    },
    extra: {
      ipAddress,
      clubId,
      printerName,
      responseCode,
      status,
      userMessage,
    },
  })
}

/**
 * ePOS SDK の onReceive ハンドラをラップし、`response.success === false` の場合に
 * Sentry に通知し、ユーザ向けトーストを表示してから元の handler を実行する。
 *
 * @template {(response: { success: boolean, code?: string|number, status?: number }) => void} H
 * @param {H|null} handler - 元の onReceive ハンドラ（不要なら null）
 * @param {object} context - reportPrinterError と同じ shape（phase は自動で 'print-response'）
 *   - context.toast: vm.$toast インスタンス（指定するとレベルに応じたトーストを自動表示）
 *   - context.printerName: トーストに表示するプリンタ名
 * @returns {H}
 */
export function wrapPrinterReceive(handler, context = {}) {
  return (response => {
    if (!response?.success) {
      const message = getPrinterStatusMessage(response?.status, context.printerName)
      reportPrinterError(
        new Error(`Printer response error: ${response?.code ?? 'unknown'}`),
        {
          ...context,
          phase: 'print-response',
          responseCode: response?.code,
          status: response?.status,
          userMessage: message?.text,
        },
      )
      if (context.toast) {
        const toastOptions = {
          onClick: () => openPrinterDiagnostic(),
        }
        if (message) {
          toastOptions.timeout = message.level === 'error' ? 6000 : 3000
          context.toast[message.level](`${message.text}（タップで診断）`, toastOptions)
        } else {
          const name = context.printerName || 'プリンタ'
          toastOptions.timeout = 6000
          context.toast.error(`${name}: 印刷に失敗しました（タップで診断）`, toastOptions)
        }
      }
    }
    if (typeof handler === 'function') handler(response)
  })
}
